/* eslint-disable max-depth */
/* eslint-disable complexity */
import { injectable } from 'inversify';
import { PsHttpService } from '@patsnap/tahiti-core';
import { StreamStatus, SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT, SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT, SSE_FIRST_RESPONSE_TIMEOUT, SSE_PROGRESS_TIMEOUT } from '../constants';
import type { PsHttpSSE, PsHttpSSEOptions, SSEResponse } from '../sse-stream-service.interface';
import { slice } from 'lodash';

/**
 * 解析 SSE 消息
 * @param input
 * @param onMessage
 * @param onFinish
 * @param onParseError
 * @param hasFinalMessage
 */
export function parseSseMessage<T = any>(rows: string[],
    onMessage: (message: SSEResponse<T>) => void,
    onFinish: () => void,
    onParseError: (error: any) => void,
    hasFinalMessage?: (data: T) => boolean
) {
    if (!rows.length) {
        return;
    }

    // 倒序循环处理每一行，如果一行是 FINISH，且有 content 字段，则触发 onMessage 和 onFinish 方法，并跳出循环
    // 如果一行是 FINISH，但没有 content 字段，则处理下一行，直到找到有 content 字段，触发 onMessage 和 onFinish 方法，并跳出循环
    // 如果一行不是 FINISH，但有 content 字段，则触发 onMessage 方法, 并跳出循环

    // 标记是否已经遇到 FINISH
    let finished = false;
    // 标记已经调用过 onFinish 方法
    let finishedCalled = false;

    function invokeOnFinish() {
        if (!finishedCalled) {
            finishedCalled = true;
            onFinish();
        }
    }

    for (let i = 0; i < rows.length; i++) {
        try {
            const sseResponse = JSON.parse(rows[i]);
            if (sseResponse?.data?.status === StreamStatus.FINISH) {
                if (hasFinalMessage && hasFinalMessage(sseResponse?.data)) {
                    try {
                        onMessage(sseResponse);
                        invokeOnFinish();
                    } catch (error) {
                    }
                }
                finished = true;
            }
            if (hasFinalMessage && hasFinalMessage(sseResponse?.data)) {
                try {
                    onMessage(sseResponse);
                    if (finished) {
                        invokeOnFinish();
                    }
                } catch (error) {
                }
            }
        } catch (error) {
            onParseError(error);
        }
    }

    if (finished) {
        invokeOnFinish();
    }
}

const closeSignal = 'Close';

const UnAuthorizedError = 401;

@injectable()
export class SSEPsHttpImpl<T extends { content: string, status: StreamStatus }> extends PsHttpService implements PsHttpSSE<T> {
    private _abortController: AbortController | undefined;

    get options() {
        return {};
    }

    private _options: PsHttpSSEOptions = {
        url: '',
        throwErrorWhenClose: false,
        hasFinalMessage: (data) => !!data,
    };

    private _onOpen: (response: Response) => void = () => { };
    private _onMessage: (data: SSEResponse<T>) => void = () => { };
    private _onError: (err: any) => void = () => { };
    private _onParseError: (error: any) => void = () => { };
    private _onClose: () => void = () => { };
    private _onFinish: () => void = () => { };

    private _customHeaders: Record<string, string> = {};

    init(options: PsHttpSSEOptions): this {
        this._options = {
            ...this._options,
            ...options,
        };
        return this;
    }

    private _disposeFirstProgressTimer = () => { };
    private _disposeProgressIntervalTimer = () => { };

    /**
     * 定时抛出 abort 错误
     * @param message 错误消息
     * @param delay 延迟时间
     * @returns 清理函数
     */
    private _throwAbortError(message: string, delay: number): () => void {
        let it: any = setTimeout(() => {
            this._abortController?.abort(message);
        }, delay);

        return () => {
            clearTimeout(it);
            it = undefined;
        };
    }

    private _oldMessageLength = 0;

    async start(): Promise<void> {
        this._abortController = new AbortController();
        this._onOpen(new Response());

        this._disposeFirstProgressTimer = this._throwAbortError(SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT, SSE_FIRST_RESPONSE_TIMEOUT);

        await this.request({
            // @ts-ignore
            method: this._options.method || 'POST',
            url: this._options.url,
            data: this._options.data,
            headers: this._customHeaders,
            onDownloadProgress: (progressEvent) => {
                if (typeof localStorage !== 'undefined' && localStorage.getItem('sseDebugOriginResponse')) {
                    console.log('sseDebugOriginResponse:', progressEvent);
                    try {
                        if (typeof window !== 'undefined') {
                            (window as any).sseDebugOriginResponse = [
                                ...((window as any).sseDebugOriginResponse || []),
                                progressEvent.target?.response
                            ]
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }
                this._disposeFirstProgressTimer();
                this._disposeProgressIntervalTimer();
                this._throwAbortError(SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT, SSE_PROGRESS_TIMEOUT);
                this._handleProgress(progressEvent);
            },
            timeout: Number.POSITIVE_INFINITY,
            signal: this._abortController.signal,
        })
            .catch((err) => {
                const abortSignal = this._abortController?.signal;
                if (abortSignal?.reason === closeSignal) {
                    if (this._options.throwErrorWhenClose) {
                        this._onError(abortSignal?.aborted ? new Error(abortSignal.reason) : err);
                    }
                } else {
                    this._onError(abortSignal?.aborted ? new Error(abortSignal.reason) : err);
                }
                throw err;
            })
            .finally(() => {
                this._disposeFirstProgressTimer();
                this._disposeProgressIntervalTimer();
                this._onClose();
                this._oldMessageLength = 0;
            });
    }

    private _handleProgress(ev: { target: { response?: string, status?: number } }) {
        const responseText = ev.target?.response || '';
        if (ev.target?.status === UnAuthorizedError || (!responseText.endsWith('\n\n') && !responseText.endsWith('\n \n'))) return
        const responseArray = responseText.split(/\n\n|\n \n/)
            .map((row) => row.trim().replace(/^data:/, ''))
            .filter((row) => row.trim())
        const finalResponse = slice(responseArray, this._oldMessageLength)
        this._oldMessageLength = responseArray.length;
        parseSseMessage(
            finalResponse,
            (message) => this._onMessage(message as SSEResponse<T>),
            () => this._onFinish(),
            (error) => this._onParseError(error),
            this._options.hasFinalMessage
        );
    }

    onOpen(callback: (response: Response) => void): this {
        this._onOpen = callback;
        return this;
    }

    onMessage(callback: (data: SSEResponse<T>) => void): this {
        this._onMessage = callback;
        return this;
    }

    onFinish(callback: () => void): this {
        this._onFinish = callback;
        return this;
    }

    onClose(callback: () => void): this {
        this._onClose = callback;
        return this;
    }

    onError(callback: (err: any) => void): this {
        this._onError = callback;
        return this;
    }

    onParseError(callback: (error: any) => void): this {
        this._onParseError = callback;
        return this;
    }

    close() {
        this._disposeFirstProgressTimer();
        this._disposeProgressIntervalTimer();
        this._abortController?.abort(closeSignal);
        this._onClose();
    }

    setFetchHeaders(customHeaders?: Record<string, string>): this {
        this._customHeaders = customHeaders || {};
        return this;
    }
}
